// Display preferences ("Appearance") and the document's download actions ("Export & Download")
// share this module's save session, positioning and dismissal handling. Both menus are
// icon-only header controls with accessible names, shown side by side at every width. The
// download buttons always call the existing export flow.
const sizes = ['xs', 'small', 'normal', 'large', 'xl']
const labels = ['Extra small', 'Small', 'Normal', 'Large', 'Extra large']
const lineSpacings = ['compact', 'normal', 'relaxed']
const lineSpacingLabels = ['Compact', 'Normal', 'Relaxed']
// Three horizontal rules whose gap shows the spacing step, so the -/+ buttons read like the
// text size A-/A+ pair without a caption row under the slider.
const lineSpacingIcon = gap => `<svg viewBox="0 0 18 18" width="18" height="18" aria-hidden="true" focusable="false"><g stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="2" y1="${9 - gap}" x2="16" y2="${9 - gap}"/><line x1="2" y1="9" x2="16" y2="9"/><line x1="2" y1="${9 + gap}" x2="16" y2="${9 + gap}"/></g></svg>`

export function buildEditorAppearance() {
  const appearanceToggle = document.querySelector('.editor-appearance-toggle')
  const downloadToggle = document.querySelector('.editor-download-toggle')
  if (!appearanceToggle) return () => {}
  const toggles = [appearanceToggle, downloadToggle].filter(Boolean)
  const sidebar = document.querySelector('[data-controller~="editor-sidebar"]')
  const visibleTrigger = () => downloadToggle || appearanceToggle
  const panel = document.createElement('div')
  panel.className = 'editor-appearance-panel'
  panel.id = 'editorAppearancePanel'
  panel.hidden = true
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-label', 'Appearance')
  panel.setAttribute('tabindex', '-1')
  panel.innerHTML = `
    <div class="editor-appearance-heading"><strong>Appearance</strong><button type="button" data-appearance-close aria-label="Close appearance menu"><span class="far fa-xmark" aria-hidden="true"></span></button></div>
    <div class="editor-appearance-label editor-appearance-label-first">Theme</div>
    <div class="editor-appearance-themes" role="group" aria-label="Editor theme"><button type="button" data-appearance-theme="light"><span class="far fa-sun" aria-hidden="true"></span> Light</button><button type="button" data-appearance-theme="dark"><span class="far fa-moon" aria-hidden="true"></span> Dark</button></div>
    <button type="button" class="editor-tools-focus" data-tools-focus aria-pressed="false" hidden><span class="far fa-expand" aria-hidden="true"></span><span>Focus mode</span><span class="editor-tools-switch" aria-hidden="true"></span></button>
    <div class="editor-appearance-label">Text size <output data-size-label></output></div>
    <div class="editor-appearance-size"><button type="button" data-size-step="-1" aria-label="Smaller text">A−</button><input type="range" min="0" max="4" step="1" aria-label="Editor text size" data-size-slider><button type="button" data-size-step="1" aria-label="Larger text">A+</button></div>
    <div class="editor-appearance-label">Line spacing <output data-line-spacing-label>Normal (default)</output></div>
    <div class="editor-appearance-size"><button type="button" data-line-spacing-step="-1" aria-label="Tighter line spacing">${lineSpacingIcon(3)}</button><input type="range" min="0" max="2" step="1" aria-label="Editor line spacing" data-line-spacing-slider><button type="button" data-line-spacing-step="1" aria-label="Looser line spacing">${lineSpacingIcon(6)}</button></div>
    <p class="editor-appearance-note" role="status" aria-live="polite">Your display preferences save automatically.</p>`
  const downloadPanel = document.createElement('div')
  downloadPanel.className = 'editor-download-panel'
  downloadPanel.id = 'editorDownloadPanel'
  downloadPanel.hidden = true
  downloadPanel.setAttribute('role', 'dialog')
  downloadPanel.setAttribute('aria-label', 'Export & Download')
  downloadPanel.setAttribute('tabindex', '-1')
  downloadPanel.innerHTML = `
    <div class="editor-appearance-heading"><strong>Export &amp; Download</strong><button type="button" data-download-close aria-label="Close export and download menu"><span class="far fa-xmark" aria-hidden="true"></span></button></div>
    <div class="editor-tools-downloads"><div class="editor-tools-formats"><button type="button" data-tools-export="word"><span class="far fa-file-word" aria-hidden="true"></span> Word</button><button type="button" data-tools-export="pdf"><span class="far fa-file-pdf" aria-hidden="true"></span> PDF</button><button type="button" data-tools-export="markdown"><span class="far fa-file-code" aria-hidden="true"></span> Markdown</button></div></div>`
  const details = document.querySelector('[data-editor-tools-details]')
  if (details) downloadPanel.appendChild(details.content.cloneNode(true))
  const panels = [panel, downloadPanel]
  panels.forEach(element => document.body.appendChild(element))
  const focusButton = panel.querySelector('[data-tools-focus]')
  const slider = panel.querySelector('[data-size-slider]')
  const lineSpacingSlider = panel.querySelector('[data-line-spacing-slider]')
  const note = panel.querySelector('.editor-appearance-note')
  let disposed = false
  let owner = appearanceToggle
  const pendingForms = new Map()
  const delivery = () => document.getElementById('publishForm')?._documentDelivery
  const content = () => document.querySelector('.editor-content')
  const currentSize = () => sizes.find(value => content()?.classList.contains(`font-size-${value}`)) || 'normal'
  const currentLineSpacing = () => lineSpacings.find(value => content()?.classList.contains(`line-spacing-${value}`)) || 'normal'
  const sync = () => {
    focusButton.hidden = innerWidth < 1400 || !sidebar
    focusButton.setAttribute('aria-pressed', String(document.body.classList.contains('editor-focus-mode')))
    const index = sizes.indexOf(currentSize())
    slider.value = index
    slider.setAttribute('aria-valuetext', labels[index])
    panel.querySelector('[data-size-label]').textContent = labels[index]
    panel.querySelectorAll('[data-size-step]').forEach(el => { el.disabled = Number(el.dataset.sizeStep) < 0 ? index === 0 : index === sizes.length - 1 })
    const lineSpacingIndex = lineSpacings.indexOf(currentLineSpacing())
    const lineSpacingLabel = lineSpacingLabels[lineSpacingIndex]
    lineSpacingSlider.value = lineSpacingIndex
    lineSpacingSlider.setAttribute('aria-valuetext', lineSpacingLabel + (lineSpacingIndex === 1 ? ' (default)' : ''))
    panel.querySelector('[data-line-spacing-label]').textContent = lineSpacingLabel + (lineSpacingIndex === 1 ? ' (default)' : '')
    panel.querySelectorAll('[data-line-spacing-step]').forEach(el => { el.disabled = Number(el.dataset.lineSpacingStep) < 0 ? lineSpacingIndex === 0 : lineSpacingIndex === lineSpacings.length - 1 })
    const theme = document.body.classList.contains('chat-theme-dark') ? 'dark' : 'light'
    panel.querySelectorAll('[data-appearance-theme]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.appearanceTheme === theme)))
  }
  const saveStatus = () => {
    const session = delivery()?.session
    const rows = ['font_size', 'line_spacing', 'theme'].map(key => session?.resources.get(`setting:${key}`)).filter(Boolean)
    const failed = rows.some(row => row.error)
    note.textContent = failed ? 'Couldn’t save your preference. Use save status to retry.' : rows.some(row => row.pending || row.dirty) ? 'Saving your preferences…' : 'Your display preferences save automatically.'
    note.classList.toggle('is-error', failed)
    const waiting = pendingForms.size > 0
    panel.querySelectorAll('[data-appearance-theme]').forEach(el => { el.disabled = waiting })
    if (waiting) {
      note.textContent = 'Saving Settings… Theme choices will be available shortly.'
      note.classList.remove('is-error')
    }
  }
  const settingsStarted = event => {
    const form = event.target
    if (!form.matches?.('form') || !form.querySelector('select[name="ai_options[theme]"]') || pendingForms.has(form)) return
    const finished = () => {
      form.removeEventListener('turbo:submit-end', finished)
      form.removeEventListener('ajax:complete', finished)
      pendingForms.delete(form)
      if (!disposed) saveStatus()
    }
    pendingForms.set(form, finished)
    // Listen on the original form: Turbo may detach it before submit-end.
    form.addEventListener('turbo:submit-end', finished)
    form.addEventListener('ajax:complete', finished)
    saveStatus()
  }
  const save = (setting, value) => {
    const owner = delivery()
    if (!owner) { note.textContent = 'The document is still loading. Try again shortly.'; return false }
    const key = owner.setSetting(setting, value)
    // The account Settings form can also persist theme. An explicit choice here
    // must reach the server even if this session last acknowledged that value.
    if (setting === 'theme') owner.session.changed(key)
    owner.session.save(key).then(() => { if (!disposed) saveStatus() })
    return true
  }
  const changeSize = index => {
    const value = sizes[Math.max(0, Math.min(4, index))]
    if (!content() || !save('font_size', value)) return
    content().classList.remove(...sizes.map(size => `font-size-${size}`))
    if (value !== 'normal') content().classList.add(`font-size-${value}`)
    document.dispatchEvent(new CustomEvent('editor-settings:changed', { detail: { setting: 'font_size', value } }))
    sync()
  }
  const changeLineSpacing = index => {
    if (!Number.isInteger(index) || index < 0 || index >= lineSpacings.length || !content() || !save('line_spacing', lineSpacings[index])) {
      sync()
      return
    }
    const value = lineSpacings[index]
    Array.from(content().classList).filter(name => name.startsWith('line-spacing-')).forEach(name => content().classList.remove(name))
    if (value !== 'normal') content().classList.add(`line-spacing-${value}`)
    document.dispatchEvent(new CustomEvent('editor-settings:changed', { detail: { setting: 'line_spacing', value } }))
    sync()
  }
  const changeTheme = theme => {
    if (pendingForms.size) return
    if (!save('theme', theme)) return
    // Keep the existing settings controls and theme holders in sync immediately.
    [document.body, document.getElementById('currentTheme'), document.getElementById('currentSidebarTheme')].filter(Boolean).forEach(el => {
      el.classList.remove('chat-theme-light', 'chat-theme-dark')
      el.classList.add(`chat-theme-${theme}`)
    })
    document.querySelectorAll('select[name="ai_options[theme]"]').forEach(el => { el.value = theme })
    document.dispatchEvent(new CustomEvent('theme:changed', { detail: { theme } }))
    sync()
  }
  const close = (restore = false) => {
    panels.forEach(menu => { menu.hidden = true })
    toggles.forEach(toggle => toggle.setAttribute('aria-expanded', 'false'))
    // Dismissal returns focus to the control that opened the menu; if a resize hid that
    // control meanwhile, the visible sibling takes it instead.
    if (restore) (owner?.getBoundingClientRect().width > 0 ? owner : visibleTrigger()).focus({ preventScroll: true })
  }
  const opened = () => panels.find(menu => !menu.hidden) || null
  const position = menu => {
    const rect = owner?.getBoundingClientRect() || appearanceToggle.getBoundingClientRect()
    const bounds = menu.getBoundingClientRect()
    menu.style.left = `${Math.max(8, Math.min(rect.right - bounds.width, innerWidth - bounds.width - 8))}px`
    menu.style.top = `${Math.max(8, Math.min(rect.bottom + 8, innerHeight - bounds.height - 8))}px`
  }
  // The desktop Focus row owns Focus mode; compact layouts use the header's Focus control.
  const firstAction = menu => menu === downloadPanel ? menu.querySelector('[data-tools-export]') : panel.querySelector('[data-appearance-theme]')
  const open = (toggle, menu, keyboardActivation) => {
    close()
    sync()
    saveStatus()
    owner = toggle
    menu.hidden = false
    toggle.setAttribute('aria-expanded', 'true')
    position(menu)
    const focusTarget = keyboardActivation ? firstAction(menu) : menu
    focusTarget.focus({ preventScroll: true })
  }
  const toggleMenu = (toggle, menu, keyboardActivation) => {
    const restore = !menu.hidden
    if (restore) { owner = toggle; close(true) } else open(toggle, menu, keyboardActivation)
  }
  const focusChanged = () => {
    // Preserve keyboard position before sync hides the desktop-only Focus row.
    if (innerWidth < 1400) close(panel.contains(document.activeElement))
    sync()
    if (opened()) position(opened())
  }
  focusButton.addEventListener('click', () => sidebar?.dispatchEvent(new CustomEvent('editor:toggle-focus')))
  sidebar?.addEventListener('editor:focus-changed', focusChanged)
  const preserveSelection = event => event.preventDefault()
  const triggers = [
    [appearanceToggle, panel],
    [downloadToggle, downloadPanel],
  ].filter(([toggle]) => !!toggle).map(([trigger, menu]) => {
    const onClick = event => toggleMenu(trigger, menu, event.detail === 0)
    trigger.addEventListener('mousedown', preserveSelection)
    trigger.addEventListener('click', onClick)
    return { trigger, menu, onClick }
  })
  panels.forEach(menu => menu.querySelectorAll('[data-tools-export]').forEach(el => el.addEventListener('click', () => {
    const exporter = delivery()
    if (!exporter) { note.textContent = 'The document is still loading. Try again shortly.'; return }
    close()
    exporter.openExport(el.dataset.toolsExport, visibleTrigger)
  })))
  panel.querySelector('[data-appearance-close]').addEventListener('click', () => close(true))
  downloadPanel.querySelector('[data-download-close]').addEventListener('click', () => close(true))
  slider.addEventListener('input', () => changeSize(Number(slider.value)))
  lineSpacingSlider.addEventListener('input', () => changeLineSpacing(Number(lineSpacingSlider.value)))
  panel.querySelectorAll('[data-size-step]').forEach(el => el.addEventListener('click', () => changeSize(sizes.indexOf(currentSize()) + Number(el.dataset.sizeStep))))
  panel.querySelectorAll('[data-line-spacing-step]').forEach(el => el.addEventListener('click', () => changeLineSpacing(lineSpacings.indexOf(currentLineSpacing()) + Number(el.dataset.lineSpacingStep))))
  panel.querySelectorAll('[data-appearance-theme]').forEach(el => el.addEventListener('click', () => changeTheme(el.dataset.appearanceTheme)))
  const outside = event => { if (!panels.some(menu => menu.contains(event.target)) && !triggers.some(({ trigger }) => trigger.contains(event.target))) close() }
  const escape = event => { if (opened() && event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); close(true) } }
  const resize = () => {
    const restore = panels.some(menu => menu.contains(document.activeElement)) || triggers.some(({ trigger }) => trigger === document.activeElement)
    close(restore)
    sync()
  }
  document.addEventListener('pointerdown', outside)
  document.addEventListener('focusin', outside)
  document.addEventListener('keydown', escape, true)
  document.addEventListener('editor-settings:changed', sync)
  document.addEventListener('theme:changed', sync)
  document.addEventListener('document-save:state', saveStatus)
  document.addEventListener('turbo:submit-start', settingsStarted)
  document.addEventListener('ajax:send', settingsStarted)
  document.addEventListener('turbo:before-cache', resize)
  window.addEventListener('resize', resize)
  return () => {
    disposed = true
    sidebar?.removeEventListener('editor:focus-changed', focusChanged)
    triggers.forEach(({ trigger, onClick }) => {
      trigger.removeEventListener('mousedown', preserveSelection)
      trigger.removeEventListener('click', onClick)
      trigger.setAttribute('aria-expanded', 'false')
    })
    document.removeEventListener('pointerdown', outside)
    document.removeEventListener('focusin', outside)
    document.removeEventListener('keydown', escape, true)
    document.removeEventListener('editor-settings:changed', sync)
    document.removeEventListener('theme:changed', sync)
    document.removeEventListener('document-save:state', saveStatus)
    document.removeEventListener('turbo:submit-start', settingsStarted)
    document.removeEventListener('ajax:send', settingsStarted)
    pendingForms.forEach((finished, form) => {
      form.removeEventListener('turbo:submit-end', finished)
      form.removeEventListener('ajax:complete', finished)
    })
    pendingForms.clear()
    document.removeEventListener('turbo:before-cache', resize)
    window.removeEventListener('resize', resize)
    panels.forEach(menu => menu.remove())
  }
}
